// src/components/insights/InsightCharts.js — the fixed, designed chart set for
// Ask Insights. The AI never draws anything: a measure returns a chart SPEC
// ({ type, points | parts, format, … }) and these components render it.
//
// Every chart measures its real width (ResizeObserver) and draws in pixels, so
// text never stretches. Hover (mouse) and drag-to-scrub (touch) both show a
// tooltip with the exact value and the measure's own detail line.
// Module-scope components only; hover lives in useState (never DOM mutation).

import React, { useEffect, useId, useRef, useState } from 'react';
import { T, colorOf, fmt } from './insightsFormat';

function useWidth() {
    const ref = useRef(null);
    const [w, setW] = useState(0);
    useEffect(() => {
        const el = ref.current;
        if (!el) return undefined;
        const set = () => setW(Math.round(el.getBoundingClientRect().width));
        set();
        if (typeof ResizeObserver === 'undefined') { window.addEventListener('resize', set); return () => window.removeEventListener('resize', set); }
        const ro = new ResizeObserver(set);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);
    return [ref, w];
}

function niceMax(v) {
    if (!(v > 0)) return 1;
    const e = 10 ** Math.floor(Math.log10(v));
    const f = v / e;
    const step = f <= 1 ? 1 : f <= 2 ? 2 : f <= 2.5 ? 2.5 : f <= 5 ? 5 : 10;
    return step * e;
}

const axisText = { fontFamily: T.sans, fontSize: 11, fill: 'color-mix(in srgb, var(--mom-charcoal) 45%, var(--mom-cream))' };

function Tooltip({ x, y, width, title, value, detail }) {
    const left = Math.max(4, Math.min(width - 168, x - 84));
    return (
        <div style={{
            position: 'absolute', left, top: Math.max(0, y - 74), width: 168, pointerEvents: 'none',
            background: 'var(--mom-charcoal)', color: '#fff', borderRadius: 12, padding: '9px 12px',
            boxShadow: '0 12px 30px rgba(62,47,47,.28)', fontFamily: T.sans, fontSize: 12, lineHeight: 1.4, zIndex: 2,
            animation: 'momInsFade .12s ease both',
        }}>
            <div style={{ fontSize: 11, opacity: 0.68 }}>{title}</div>
            <div style={{ fontFamily: T.serif, fontSize: 17, fontWeight: 600 }}>{value}</div>
            {detail && <div style={{ fontSize: 11.5, opacity: 0.8 }}>{detail}</div>}
        </div>
    );
}

// Shared hover/scrub: maps a pointer x to the nearest point index.
function useScrub(count, xOf) {
    const [idx, setIdx] = useState(null);
    const handlers = {
        onPointerMove: (e) => {
            const r = e.currentTarget.getBoundingClientRect();
            const px = e.clientX - r.left;
            let best = 0; let bd = Infinity;
            for (let i = 0; i < count; i += 1) { const d = Math.abs(xOf(i) - px); if (d < bd) { bd = d; best = i; } }
            setIdx(best);
        },
        onPointerLeave: () => setIdx(null),
        onPointerDown: (e) => { if (e.pointerType !== 'mouse') e.currentTarget.setPointerCapture?.(e.pointerId); },
    };
    return [idx, handlers];
}

// ── Line (trend over time) ──
export function LineChart({ points = [], format = 'int', average, averageLabel, height = 240 }) {
    const [ref, w] = useWidth();
    const gid = useId().replace(/:/g, '');
    const padL = 40; const padR = 16; const padT = 18; const padB = 30;
    const n = points.length;
    const max = niceMax(Math.max(...points.map((p) => p.y || 0), average || 0) * 1.08);
    const iw = Math.max(10, w - padL - padR); const ih = height - padT - padB;
    const xOf = (i) => padL + (n <= 1 ? iw / 2 : (iw * i) / (n - 1));
    const yOf = (v) => padT + ih - (ih * (v || 0)) / max;
    const [hi, handlers] = useScrub(n, xOf);
    const solid = points.filter((p) => !p.partial);
    const path = (arr, offset = 0) => arr.map((p, i) => `${i ? 'L' : 'M'}${xOf(i + offset).toFixed(1)} ${yOf(p.y).toFixed(1)}`).join(' ');
    const last = n - 1;
    const partialSeg = points[last]?.partial && n > 1 ? `M${xOf(last - 1)} ${yOf(points[last - 1].y)} L${xOf(last)} ${yOf(points[last].y)}` : null;
    const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => f * max);
    const labelEvery = w < 420 ? Math.ceil(n / 6) : 1;
    return (
        <div ref={ref} style={{ position: 'relative', width: '100%', touchAction: 'pan-y' }}>
            {w > 0 && (
                <svg width={w} height={height} style={{ display: 'block', cursor: 'crosshair' }} {...handlers}>
                    <defs><linearGradient id={`g${gid}`} x1="0" x2="0" y1="0" y2="1"><stop offset="0" stopColor="var(--mom-primary, #c33764)" stopOpacity="0.16" /><stop offset="1" stopColor="var(--mom-primary, #c33764)" stopOpacity="0" /></linearGradient></defs>
                    {ticks.map((t) => (
                        <g key={t}>
                            <line x1={padL} x2={w - padR} y1={yOf(t)} y2={yOf(t)} stroke={T.grid} />
                            <text x={padL - 8} y={yOf(t) + 4} textAnchor="end" style={axisText}>{fmt(t, format === 'money' ? 'money0' : format === 'pct' ? 'pct' : 'int')}</text>
                        </g>
                    ))}
                    {average != null && averageLabel !== null && (
                        <g>
                            <line x1={padL} x2={w - padR} y1={yOf(average)} y2={yOf(average)} stroke="#c1848c" strokeDasharray="4 5" strokeWidth="1.2" />
                            <text x={padL + 8} y={yOf(average) - 6} style={{ ...axisText, fill: '#b06f78', fontWeight: 600 }}>{averageLabel || 'Average'} {fmt(average, format)}</text>
                        </g>
                    )}
                    {solid.length > 1 && <path d={`${path(solid)} L${xOf(solid.length - 1)} ${padT + ih} L${xOf(0)} ${padT + ih} Z`} fill={`url(#g${gid})`} />}
                    {solid.length > 1 && <path d={path(solid)} fill="none" stroke="var(--mom-primary, #c33764)" strokeWidth="2.6" strokeLinejoin="round" strokeLinecap="round" />}
                    {partialSeg && <path d={partialSeg} fill="none" stroke="var(--mom-primary, #c33764)" strokeWidth="2" strokeDasharray="4 4" opacity="0.7" />}
                    {hi != null && <line x1={xOf(hi)} x2={xOf(hi)} y1={padT} y2={padT + ih} stroke="var(--mom-charcoal)" strokeOpacity="0.18" strokeDasharray="3 3" />}
                    {points.map((p, i) => (
                        <circle key={p.key || i} cx={xOf(i)} cy={yOf(p.y)} r={i === hi ? 5 : i === last ? 5.5 : 3.4}
                            fill={i === last || i === hi ? 'var(--mom-primary, #c33764)' : '#fff'}
                            stroke={i === last && !p.partial ? '#fff' : 'var(--mom-primary, #c33764)'} strokeWidth={i === last ? 2.5 : 2}
                            strokeDasharray={p.partial ? '2 2' : undefined} />
                    ))}
                    {points.map((p, i) => (i % labelEvery === 0 || i === last) && (
                        <text key={`x${i}`} x={xOf(i)} y={height - 8} textAnchor="middle" style={{ ...axisText, fill: i === last ? 'var(--mom-charcoal)' : axisText.fill, fontWeight: i === last ? 700 : 400 }}>{p.x}</text>
                    ))}
                </svg>
            )}
            {hi != null && points[hi] && (
                <Tooltip x={xOf(hi)} y={yOf(points[hi].y)} width={w} title={`${points[hi].x}${points[hi].partial ? ' (so far)' : ''}`} value={fmt(points[hi].y, format)} detail={points[hi].detail} />
            )}
        </div>
    );
}

// ── Diverging (joined above, ended below) ──
export function DivergingChart({ points = [], upLabel = 'Up', downLabel = 'Down', height = 250 }) {
    const [ref, w] = useWidth();
    const padL = 12; const padR = 12; const padT = 22; const padB = 30;
    const n = points.length;
    const max = niceMax(Math.max(1, ...points.map((p) => Math.max(p.up || 0, p.down || 0))));
    const iw = Math.max(10, w - padL - padR); const ih = height - padT - padB;
    const mid = padT + ih / 2;
    const band = iw / Math.max(1, n);
    const bw = Math.min(34, band * 0.52);
    const xOf = (i) => padL + band * i + band / 2;
    const hOf = (v) => ((ih / 2 - 14) * (v || 0)) / max;
    const [hi, handlers] = useScrub(n, xOf);
    return (
        <div ref={ref} style={{ position: 'relative', width: '100%', touchAction: 'pan-y' }}>
            {w > 0 && (
                <svg width={w} height={height} style={{ display: 'block' }} {...handlers}>
                    <line x1={padL} x2={w - padR} y1={mid} y2={mid} stroke={T.line} />
                    {points.map((p, i) => {
                        const x = xOf(i) - bw / 2; const hu = hOf(p.up); const hd = hOf(p.down);
                        const dim = hi != null && hi !== i ? 0.45 : 1;
                        return (
                            <g key={p.key || i} opacity={dim}>
                                <rect x={x} y={mid - hu} width={bw} height={Math.max(hu, 0.5)} rx="5" fill="var(--mom-primary, #c33764)" opacity={p.partial ? 0.55 : 1} />
                                <rect x={x} y={mid} width={bw} height={Math.max(hd, 0.5)} rx="5" fill="#c1848c" opacity={p.partial ? 0.5 : 0.85} />
                                {w > 380 && <text x={xOf(i)} y={mid - hu - 6} textAnchor="middle" style={{ ...axisText, fill: 'var(--mom-charcoal)', fontWeight: 600 }}>{p.up}</text>}
                                {w > 380 && <text x={xOf(i)} y={mid + hd + 14} textAnchor="middle" style={axisText}>{p.down}</text>}
                                <text x={xOf(i)} y={height - 8} textAnchor="middle" style={{ ...axisText, fontWeight: i === n - 1 ? 700 : 400, fill: i === n - 1 ? 'var(--mom-charcoal)' : axisText.fill }}>{p.x}</text>
                            </g>
                        );
                    })}
                    <text x={padL} y={12} style={{ ...axisText, fill: 'var(--mom-primary, #c33764)', fontWeight: 700 }}>▲ {upLabel}</text>
                    <text x={padL + 92} y={12} style={{ ...axisText, fill: '#b06f78', fontWeight: 700 }}>▼ {downLabel}</text>
                </svg>
            )}
            {hi != null && points[hi] && (
                <Tooltip x={xOf(hi)} y={mid} width={w} title={`${points[hi].x}${points[hi].partial ? ' (so far)' : ''}`}
                    value={`+${points[hi].up} / −${points[hi].down}`} detail={`${upLabel} ${points[hi].up} · ${downLabel} ${points[hi].down} · net ${(points[hi].up - points[hi].down) >= 0 ? '+' : ''}${points[hi].up - points[hi].down}`} />
            )}
        </div>
    );
}

// ── Vertical bars (a handful of categories) and columns (many: days, hours) ──
export function BarsChart({ points = [], format = 'int', height = 230, dense = false }) {
    const [ref, w] = useWidth();
    const padL = 8; const padR = 8; const padT = 22; const padB = 30;
    const n = points.length;
    const max = niceMax(Math.max(1, ...points.map((p) => p.y || 0)) * 1.05);
    const iw = Math.max(10, w - padL - padR); const ih = height - padT - padB;
    const band = iw / Math.max(1, n);
    const bw = dense ? Math.max(2, band * 0.66) : Math.min(64, band * 0.58);
    const xOf = (i) => padL + band * i + band / 2;
    const [hi, handlers] = useScrub(n, xOf);
    const labelEvery = dense ? Math.ceil(n / Math.max(4, Math.floor(iw / 44))) : 1;
    const peak = points.reduce((b, p, i) => ((p.y || 0) > (points[b]?.y || 0) ? i : b), 0);
    return (
        <div ref={ref} style={{ position: 'relative', width: '100%', touchAction: 'pan-y' }}>
            {w > 0 && (
                <svg width={w} height={height} style={{ display: 'block' }} {...handlers}>
                    <line x1={padL} x2={w - padR} y1={padT + ih} y2={padT + ih} stroke={T.line} />
                    {points.map((p, i) => {
                        const h = (ih * (p.y || 0)) / max;
                        const on = hi === i || (hi == null && i === peak);
                        return (
                            <g key={p.key || `${p.x}${i}`}>
                                <rect x={xOf(i) - bw / 2} y={padT + ih - h} width={bw} height={Math.max(h, 0.5)} rx={dense ? 2 : 7}
                                    fill={on ? 'var(--mom-primary, #c33764)' : colorOf(p.color, 0) === colorOf('primary') ? '#e8c3c6' : colorOf(p.color)}
                                    style={{ transition: 'fill .15s' }} />
                                {!dense && <text x={xOf(i)} y={padT + ih - h - 7} textAnchor="middle" style={{ ...axisText, fill: 'var(--mom-charcoal)', fontWeight: 600 }}>{fmt(p.y, format)}</text>}
                                {(i % labelEvery === 0) && <text x={xOf(i)} y={height - 9} textAnchor="middle" style={axisText}>{String(p.x).length > 14 && !dense ? `${String(p.x).slice(0, 13)}…` : p.x}</text>}
                            </g>
                        );
                    })}
                </svg>
            )}
            {hi != null && points[hi] && <Tooltip x={xOf(hi)} y={padT + 30} width={w} title={points[hi].x} value={fmt(points[hi].y, format)} detail={points[hi].detail} />}
        </div>
    );
}

// ── Composition (one stacked bar + legend) ──
export function CompositionChart({ parts = [], format = 'int', showShare }) {
    const [hover, setHover] = useState(null);
    const total = parts.reduce((a, p) => a + (p.value || 0), 0) || 1;
    return (
        <div>
            <div style={{ display: 'flex', height: 44, borderRadius: 14, overflow: 'hidden', background: T.hair }}>
                {parts.map((p, i) => {
                    const share = (p.value / total) * 100;
                    return (
                        <div key={p.label} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}
                            style={{
                                width: `${share}%`, background: colorOf(p.color, i), opacity: hover != null && hover !== i ? 0.45 : 1,
                                display: 'flex', alignItems: 'center', paddingLeft: share > 9 ? 12 : 0, color: '#fff',
                                fontFamily: T.sans, fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', transition: 'opacity .15s',
                                borderRight: i < parts.length - 1 ? '2px solid rgba(255,255,255,.7)' : 'none',
                            }}>
                            {share > 14 ? `${p.label} ${Math.round(share)}%` : share > 9 ? `${Math.round(share)}%` : ''}
                        </div>
                    );
                })}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(118px, 1fr))', gap: '14px 16px', marginTop: 18 }}>
                {parts.map((p, i) => (
                    <div key={p.label} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)} style={{ opacity: hover != null && hover !== i ? 0.55 : 1, transition: 'opacity .15s' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontFamily: T.sans, fontSize: 12, fontWeight: 600, color: T.soft }}>
                            <span style={{ width: 10, height: 10, borderRadius: 3, background: colorOf(p.color, i), flex: '0 0 auto' }} />{p.label}
                        </div>
                        <div style={{ fontFamily: T.serif, fontSize: 22, fontWeight: 600, color: T.ink, marginTop: 3 }}>{fmt(p.value, format)}</div>
                        <div style={{ fontFamily: T.sans, fontSize: 11.5, color: T.faint }}>{showShare || p.share != null ? `${p.share != null ? p.share : Math.round((p.value / total) * 1000) / 10}% of total` : (p.detail || '')}</div>
                    </div>
                ))}
            </div>
        </div>
    );
}

// ── Ranked (horizontal bars with labels) ──
export function RankedChart({ points = [], format = 'int', caption, limit = 12 }) {
    const [hover, setHover] = useState(null);
    const shown = points.slice(0, limit);
    const max = Math.max(1, ...shown.map((p) => p.y || 0));
    return (
        <div>
            {caption && <div style={{ fontFamily: T.sans, fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: T.faint, marginBottom: 10 }}>{caption}</div>}
            {shown.map((p, i) => (
                <div key={`${p.x}${i}`} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}
                    style={{ display: 'grid', gridTemplateColumns: 'minmax(90px, 34%) 1fr auto', gap: 12, alignItems: 'center', padding: '6px 0' }}>
                    <span title={p.x} style={{ fontFamily: T.sans, fontSize: 13, color: T.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.x}</span>
                    <span style={{ height: 9, borderRadius: 99, background: T.hair, overflow: 'hidden' }}>
                        <span style={{ display: 'block', height: '100%', width: `${((p.y || 0) / max) * 100}%`, borderRadius: 99, background: p.color ? colorOf(p.color) : i === 0 || hover === i ? 'var(--mom-primary, #c33764)' : '#d9a3aa', transition: 'width .6s cubic-bezier(.2,.8,.2,1), background .15s' }} />
                    </span>
                    <span style={{ fontFamily: T.sans, fontSize: 13, fontWeight: 700, color: T.ink, minWidth: 44, textAlign: 'right' }}>
                        {fmt(p.y, format)}{hover === i && p.detail ? <span style={{ fontWeight: 500, color: T.faint, marginLeft: 6 }}>{p.detail}</span> : null}
                    </span>
                </div>
            ))}
        </div>
    );
}

// ── Tile minis ──
export function Sparkline({ values = [], height = 36 }) {
    const [ref, w] = useWidth();
    const gid = useId().replace(/:/g, '');
    const v = values.filter((x) => x != null);
    if (v.length < 2) return <div ref={ref} style={{ height }} />;
    const min = Math.min(...v); const max = Math.max(...v);
    const span = max - min || 1;
    const xOf = (i) => 2 + ((w - 6) * i) / (v.length - 1);
    const yOf = (x) => 4 + (height - 8) * (1 - (x - min) / span);
    const d = v.map((x, i) => `${i ? 'L' : 'M'}${xOf(i).toFixed(1)} ${yOf(x).toFixed(1)}`).join(' ');
    return (
        <div ref={ref} style={{ width: '100%', height }}>
            {w > 0 && (
                <svg width={w} height={height} style={{ display: 'block' }}>
                    <defs><linearGradient id={`s${gid}`} x1="0" x2="0" y1="0" y2="1"><stop offset="0" stopColor="var(--mom-primary, #c33764)" stopOpacity="0.2" /><stop offset="1" stopColor="var(--mom-primary, #c33764)" stopOpacity="0" /></linearGradient></defs>
                    <path d={`${d} L${xOf(v.length - 1)} ${height} L${xOf(0)} ${height} Z`} fill={`url(#s${gid})`} />
                    <path d={d} fill="none" stroke="var(--mom-primary, #c33764)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <circle cx={xOf(v.length - 1)} cy={yOf(v[v.length - 1])} r="3" fill="var(--mom-primary, #c33764)" />
                </svg>
            )}
        </div>
    );
}

export function MiniBars({ values = [], height = 36 }) {
    const max = Math.max(1, ...values);
    return (
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height }}>
            {values.map((v, i) => (
                <span key={i} style={{ flex: 1, height: `${Math.max(6, (v / max) * 100)}%`, borderRadius: 4, background: i === values.length - 1 ? 'var(--mom-primary, #c33764)' : '#efd3d5' }} />
            ))}
        </div>
    );
}

export function ChartFor({ chart }) {
    if (!chart) return null;
    switch (chart.type) {
        case 'line': return <LineChart {...chart} />;
        case 'diverging': return <DivergingChart {...chart} />;
        case 'bars': return <BarsChart {...chart} />;
        case 'columns': return <BarsChart {...chart} dense />;
        case 'composition': return <CompositionChart {...chart} />;
        case 'ranked': return <RankedChart {...chart} />;
        default: return null;
    }
}
